import type { LifecycleAlign, LifecycleCurve } from '@/api/revenueLeakage'
import { formatCurrency, formatInteger, formatPercent, type FormatOptions } from '@/design'
import { ChartDataTable, ChartFrame, ChartYGrid, labelStep, useChartBox, useChartCursor } from '@/design/chartParts'
import { niceMax } from '@/design/TrendChart'

/** A curve as the chart draws it: its competition and its fixed colour slot (1–6). */
export interface ChartSeries {
  curve: LifecycleCurve
  slot: number
}

const HEIGHT = 260
const PAD = { left: 60, right: 64, top: 16, bottom: 28 }
const Y_TICKS = 4
const MAX_X_LABELS = 7

/**
 * Each slot's line pattern beside its colour: six colours cannot all be told apart by
 * every reader (the palette's all-pairs check fails past three), so slots 4–6 are dashed
 * and every series ends in its own marker shape, named in the legend.
 */
const DASHES: Record<number, string | undefined> = { 4: '7 4', 5: '2 3', 6: '9 3 2 3' }

/** The end-point marker of each slot: circle, square, triangle, diamond, triangle down, bar. */
export function EndMarker({ slot, x, y }: { slot: number; x: number; y: number }) {
  const r = 4.5
  const shapes: Record<number, string> = {
    2: `M${x - r},${y - r} h${2 * r} v${2 * r} h${-2 * r} Z`,
    3: `M${x},${y - r - 1} L${x + r + 1},${y + r} L${x - r - 1},${y + r} Z`,
    4: `M${x},${y - r - 1} L${x + r + 1},${y} L${x},${y + r + 1} L${x - r - 1},${y} Z`,
    5: `M${x - r - 1},${y - r} L${x + r + 1},${y - r} L${x},${y + r + 1} Z`,
    6: `M${x - r - 1},${y - 2} h${2 * r + 2} v4 h${-2 * r - 2} Z`,
  }
  const path = shapes[slot]
  return path ? (
    <path d={path} className="rl-life__marker" data-slot={slot} />
  ) : (
    <circle cx={x} cy={y} r={r} className="rl-life__marker" data-slot={slot} />
  )
}

/** The legend's key: the slot's line and marker, drawn small. */
export function SeriesKey({ slot }: { slot: number }) {
  return (
    <svg width="26" height="12" viewBox="0 0 26 12" aria-hidden="true" focusable="false">
      <line x1="1" x2="16" y1="6" y2="6" className="rl-life__line" data-slot={slot} strokeDasharray={DASHES[slot]} />
      <EndMarker slot={slot} x={20} y={6} />
    </svg>
  )
}

/** Whether a curve is still running: it ends today rather than on its draw day. */
export function running(curve: LifecycleCurve): boolean {
  // A cut curve's last point is not today: the API kept its first 400 days.
  if (curve.truncated) return false
  const last = curve.points[curve.points.length - 1]
  return curve.drawAt === null || (last !== undefined && last.date < curve.drawAt.slice(0, 10))
}

function dayName(day: number, align: LifecycleAlign): string {
  if (align === 'launch') return day === 0 ? 'Launch day' : day > 0 ? `Day ${day}` : `${-day} ${-day === 1 ? 'day' : 'days'} before launch`
  return day === 0 ? 'Draw day' : day < 0 ? `${-day} ${-day === 1 ? 'day' : 'days'} to draw` : `${day} ${day === 1 ? 'day' : 'days'} after draw`
}

/**
 * Up to six competitions' cumulative revenue by day since launch (or to the draw), on one
 * axis: each in its fixed colour and line pattern, ending in its own marker, with "Sold
 * out" where a curve reached capacity and "Today" where one is still on sale. Hover and
 * the arrow keys read every competition's figures for the day; the whole chart is a
 * visually hidden table too.
 */
export function LifecycleChart({ series, align, money }: { series: readonly ChartSeries[]; align: LifecycleAlign; money: FormatOptions }) {
  const box = useChartBox(HEIGHT, PAD)
  const days = series.flatMap(({ curve }) => curve.points.map((point) => point.day))
  const first = days.length ? Math.min(...days) : 0
  const last = days.length ? Math.max(...days) : 0
  const span = Math.max(1, last - first)
  const cursor = useChartCursor(last - first + 1, box.width, (x) => Math.round(((x - box.left) / box.innerWidth) * span))
  if (series.length === 0 || days.length === 0) return null

  const values = series.flatMap(({ curve }) => curve.points.map((point) => point.cumulativeRevenue))
  const high = niceMax(Math.max(0, ...values))
  const low = Math.min(0, ...values) < 0 ? -niceMax(-Math.min(...values)) : 0
  const x = (day: number) => box.left + ((day - first) / span) * box.innerWidth
  const y = (value: number) => box.bottom - ((value - low) / (high - low)) * box.innerHeight
  const amount = (value: number, compact = false) => formatCurrency(value, { ...money, compact })
  const activeDay = cursor.active === null ? null : first + cursor.active
  const every = labelStep(last - first + 1, MAX_X_LABELS)
  const title = `Cumulative revenue by day since ${align === 'launch' ? 'launch' : 'the draw'}`
  const pointAt = (curve: LifecycleCurve, day: number) => curve.points.find((point) => point.day === day)

  return (
    <ChartFrame
      title={title}
      box={box}
      cursor={cursor}
      className="rl-life"
      legendClassName="rl-chart__legend"
      hint="read each day"
      readout={
        activeDay === null
          ? null
          : {
              at: x(activeDay),
              content: (
                <>
                  <strong>{dayName(activeDay, align)}</strong>
                  {series.map(({ curve, slot }) => {
                    const point = pointAt(curve, activeDay)
                    return (
                      <span key={curve.competitionId} className="rl-chart__key">
                        <SeriesKey slot={slot} /> {curve.name}:{' '}
                        {point ? `${amount(point.cumulativeRevenue)} (${amount(point.revenue)} that day)` : 'not on sale'}
                      </span>
                    )
                  })}
                </>
              ),
            }
      }
      legend={series.map(({ curve, slot }) => (
        <span key={curve.competitionId} className="rl-chart__key">
          <SeriesKey slot={slot} />
          {curve.name}
        </span>
      ))}
      table={
        <ChartDataTable
          title={title}
          columns={['Competition', 'Days', 'Revenue', 'Tickets', 'Sold', 'Sold out']}
          rows={series.map(({ curve }) => {
            const end = curve.points[curve.points.length - 1]
            return {
              key: curve.competitionId,
              name: curve.name,
              cells: [
                end ? `${dayName(curve.points[0]?.day ?? 0, align)} to ${dayName(end.day, align).toLowerCase()}` : '—',
                amount(curve.totals.revenue),
                formatInteger(curve.totals.tickets, money),
                end?.cumulativeSoldFraction === null || end === undefined ? 'No capacity' : formatPercent(end.cumulativeSoldFraction),
                curve.soldOutDay === null ? 'No' : dayName(curve.soldOutDay, align),
              ],
            }
          })}
        />
      }
    >
      <ChartYGrid min={low} max={high} ticks={Y_TICKS} left={box.left} right={box.right} y={y} label={(value) => amount(value, true)} />
      {Array.from({ length: last - first + 1 }, (_, index) => first + index)
        .filter((day) => (day - first) % every === 0)
        .map((day) => (
          <text key={day} className="ci-trend__x" x={x(day)} y={box.height - 8} textAnchor="middle">
            {day}
          </text>
        ))}
      {series.map(({ curve, slot }) => {
        const path = curve.points.map((point, index) => `${index === 0 ? 'M' : 'L'}${x(point.day).toFixed(1)},${y(point.cumulativeRevenue).toFixed(1)}`).join(' ')
        const end = curve.points[curve.points.length - 1]
        const soldOut = curve.soldOutDay === null ? undefined : pointAt(curve, curve.soldOutDay)
        return (
          <g key={curve.competitionId} data-testid="lifecycle-series" data-slot={slot}>
            <path d={path} className="rl-life__line" data-slot={slot} strokeDasharray={DASHES[slot]} />
            {soldOut ? (
              <g className="rl-chart__label" data-testid="sold-out-label">
                <line x1={x(soldOut.day)} x2={x(soldOut.day)} y1={y(soldOut.cumulativeRevenue) - 4} y2={y(soldOut.cumulativeRevenue) - 12} />
                <text x={x(soldOut.day)} y={y(soldOut.cumulativeRevenue) - 15} textAnchor="middle">
                  Sold out
                </text>
              </g>
            ) : null}
            {end ? (
              <>
                <EndMarker slot={slot} x={x(end.day)} y={y(end.cumulativeRevenue)} />
                {running(curve) ? (
                  <text className="rl-chart__label" data-testid="today-label" x={x(end.day) + 8} y={y(end.cumulativeRevenue) + 4}>
                    Today
                  </text>
                ) : null}
              </>
            ) : null}
          </g>
        )
      })}
      {activeDay !== null ? (
        <g className="ci-trend__cursor">
          <line x1={x(activeDay)} x2={x(activeDay)} y1={box.top} y2={box.bottom} />
        </g>
      ) : null}
    </ChartFrame>
  )
}
